/**
 * Checks whether a field should be visible depending on the context ('row' or 'dialog').
 *
 * The field config can define:
 *  - `showIfRow: (row) => boolean` — for rendering condition in the table row.
 *  - `showIfDialog: (data) => boolean` — for rendering condition in the edit/add dialog.
 *
 * @param {Object} field - Column or editor config object.
 * @param {Object} data - Current row or dialog data.
 * @param {'row' | 'dialog'} [context='row'] - The rendering context ('row' or 'dialog').
 * @returns {boolean} - Whether to show the field or not.
 */
export const isFieldVisible = (field, data, context = 'row') => {
    if (!['row', 'dialog'].includes(context)) {
        console.warn(`[isFieldVisible] Unknown context "${context}". Expected 'row' or 'dialog'. Defaulting to 'row'.`);
        context = 'row';
    }

    const condition =
        context === 'row' ? field.showIfRow : field.showIfDialog;
    return typeof condition === 'function' ? condition(data) : true;
};

/**
 * Checks whether a given column should be visible based on the provided row data.
 *
 * A column is considered visible if:
 * - It has no `showIfRow` condition (i.e., it's always visible), OR
 * - The `showIfRow` function returns `true` for at least one row.
 *
 * This utility is intended to be used in grid/table systems where column visibility
 * depends on row-specific conditions (e.g., role-based field access).
 *
 * @param {Object} field - A column configuration object, may include `showIfRow`.
 * @param {Array<Object>} rows - The full array of row data from the table.
 * @returns {boolean} `true` if the column should be shown in the table header.
 */
export const isColumnVisibleInAnyRow = (field, rows) => {
    if (typeof field.showIfRow !== 'function') return true;
    return rows.some((row) => field.showIfRow(row));
};

/**
 * Filters the full list of columns and returns only those that should be displayed
 * in the table based on the current dataset.
 *
 * Useful when you want a dynamic table header that reflects the fields actually
 * visible in the current context (e.g., filtered user roles, permission matrix).
 *
 * Columns will only be shown if:
 * - `displayGrid` is true, AND
 * - `showIfRow` returns true for at least one row (if such condition exists).
 *
 * @param {Array<Object>} headLabel - Array of column definition objects.
 * @param {Array<Object>} rows - Array of all visible or filtered rows in the table.
 * @returns {Array<Object>} The filtered array of visible column definitions.
 */
export const getVisibleColumns = (headLabel, rows) => {
    return headLabel.filter((field) => {
        if (!field.displayGrid) return false;
        return isColumnVisibleInAnyRow(field, rows);
    });
};
